import { useCallback, useMemo, useState, type FormEvent } from 'react';
import type { TenantOverviewDTO } from '../../../types/lac';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksTextFormField } from '../../../components/liks/LiksTextFormField/LiksTextFormField';
import { CostPricesDialog } from '../../../features/costs/components/CostPricesDialog';
import { CostSummary } from '../../../features/costs/components/CostSummary';
import { LacTenantCostChart } from '../../../features/lac/components/LacTenantCostChart';
import { useSaveLacCostLimitMutation } from '../../../features/lac/mutations/useLacCostLimitMutations';
import { useLacTenantOverviewQuery } from '../../../features/lac/queries/useLacTenantOverviewsQuery';
import { getCurrentYearDateRange } from '../../../features/lac/tenantOverviewUtils';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import '../LacTenantDetailsPage.css';
import './LacTenantCostsTab.css';

export type LacTenantCostsTabProps = {
  tenantName: string;
  overview: TenantOverviewDTO | null | undefined;
  monthlyCostLimit?: number | null;
  yearlyCostLimit?: number | null;
  isOverviewLoading?: boolean;
  isOverviewError?: boolean;
};

function parsePositiveLimit(raw: string): {
  value: number | null;
  invalid: boolean;
} {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { value: null, invalid: false };
  }
  const parsed = Number(trimmed);
  return {
    value: Number.isFinite(parsed) ? parsed : null,
    invalid: !Number.isFinite(parsed) || parsed <= 0,
  };
}

export function LacTenantCostsTab({
  tenantName,
  overview,
  monthlyCostLimit = null,
  yearlyCostLimit = null,
  isOverviewLoading = false,
  isOverviewError = false,
}: LacTenantCostsTabProps) {
  const { translate } = useI18n();
  const saveCostLimitMutation = useSaveLacCostLimitMutation();
  const [pricesOpen, setPricesOpen] = useState(false);
  const closePrices = useCallback(() => setPricesOpen(false), []);
  const yearRange = useMemo(() => getCurrentYearDateRange(), []);
  const yearOverviewQuery = useLacTenantOverviewQuery(tenantName, yearRange);

  const [monthlyLimit, setMonthlyLimit] = useState(
    monthlyCostLimit != null ? String(monthlyCostLimit) : '',
  );
  const [yearlyLimit, setYearlyLimit] = useState(
    yearlyCostLimit != null ? String(yearlyCostLimit) : '',
  );

  const isSubmitting = saveCostLimitMutation.isPending;
  const parsedMonthly = parsePositiveLimit(monthlyLimit);
  const parsedYearly = parsePositiveLimit(yearlyLimit);
  const hasAtLeastOneLimit =
    parsedMonthly.value != null || parsedYearly.value != null;
  const yearlyExceedsMonthlyBound =
    parsedMonthly.value != null &&
    parsedYearly.value != null &&
    parsedYearly.value > 12 * parsedMonthly.value;
  const monthlyChanged =
    parsedMonthly.value != null && parsedMonthly.value !== monthlyCostLimit;
  const yearlyChanged =
    parsedYearly.value != null && parsedYearly.value !== yearlyCostLimit;
  const isSaveDisabled =
    isSubmitting ||
    parsedMonthly.invalid ||
    parsedYearly.invalid ||
    yearlyExceedsMonthlyBound ||
    !hasAtLeastOneLimit ||
    (!monthlyChanged && !yearlyChanged);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaveDisabled) {
      return;
    }

    saveCostLimitMutation.mutate(
      {
        tenantName,
        monthlyLimit: parsedMonthly.value,
        yearlyLimit: parsedYearly.value,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('lac.organisations.costs.saveSuccessTitle'),
            description: translate(
              'lac.organisations.costs.saveSuccessDescription',
            ),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('lac.organisations.costs.saveErrorTitle'),
            translate('lac.organisations.costs.saveErrorDescription'),
          );
        },
      },
    );
  };

  return (
    <div className="lac-tenant-details__tab-content">
      <div className="lac-tenant-costs__top">
        <form
          className="lac-tenant-details__form lac-tenant-costs__limit-form"
          onSubmit={handleSubmit}
        >
          <div className="lac-tenant-details__card">
            <div className="lac-tenant-details__body lac-tenant-details__body--single">
              <div className="lac-tenant-details__fields">
                <LiksTextFormField
                  label={translate(
                    'lac.organisations.details.monthlyCostLimitLabel',
                  )}
                  type="number"
                  value={monthlyLimit}
                  onChange={setMonthlyLimit}
                  disabled={isSubmitting}
                  autoComplete="off"
                  min={0.01}
                  step={0.01}
                  error={
                    parsedMonthly.invalid
                      ? translate(
                          'lac.organisations.details.monthlyCostLimitInvalid',
                        )
                      : null
                  }
                />
                <LiksTextFormField
                  label={translate(
                    'lac.organisations.details.yearlyCostLimitLabel',
                  )}
                  type="number"
                  value={yearlyLimit}
                  onChange={setYearlyLimit}
                  disabled={isSubmitting}
                  autoComplete="off"
                  min={0.01}
                  step={0.01}
                  error={
                    parsedYearly.invalid
                      ? translate(
                          'lac.organisations.details.yearlyCostLimitInvalid',
                        )
                      : yearlyExceedsMonthlyBound
                        ? translate(
                            'lac.organisations.details.yearlyExceedsMonthlyBound',
                          )
                        : !hasAtLeastOneLimit
                          ? translate(
                              'lac.organisations.details.atLeastOneCostLimitRequired',
                            )
                          : null
                  }
                />
              </div>
            </div>
            <div className="lac-tenant-details__actions">
              <LiksButton type="submit" disabled={isSaveDisabled}>
                {translate('lac.organisations.costs.save')}
              </LiksButton>
            </div>
          </div>
        </form>

        <section
          className="lac-tenant-costs__summary"
          aria-label={translate('lac.organisations.costs.legend')}
        >
          <CostSummary
            isLoading={isOverviewLoading}
            isError={isOverviewError}
            hasData={Boolean(overview)}
            monthlyEstimatedCosts={overview?.tokenData?.estimatedCosts}
            yearlyEstimatedCosts={
              yearOverviewQuery.data?.tokenData?.estimatedCosts
            }
            monthlyCostLimit={monthlyCostLimit}
            yearlyCostLimit={yearlyCostLimit}
            onOpenPrices={() => setPricesOpen(true)}
          />
        </section>
      </div>

      <LacTenantCostChart
        tenantName={tenantName}
        monthlyCostLimit={monthlyCostLimit}
        yearlyCostLimit={yearlyCostLimit}
        enabled={Boolean(tenantName.trim())}
      />

      <CostPricesDialog isOpen={pricesOpen} onClose={closePrices} />
    </div>
  );
}
